<template>
  <div
    class="absolute top-0 bottom-0 w-full z-[3] flex flex-col overflow-hidden"
  >
    <!-- Background blur -->
    <div class="absolute inset-0 backdrop-blur-[5px]"></div>

    <!-- Radial gradient overlay -->
    <div class="absolute inset-0 radial-gradient-overlay"></div>

    <div class="relative h-full w-full py-12 gap-12 overflow-auto">
      <div class="z-10 max-w-3xl mx-auto px-4 md:mb-12 text-center">
        <!-- Success indicator -->
        <div class="flex justify-center mb-6">
          <div class="relative">
            <div
              class="p-4 sm:p-6 bg-gradient-to-br from-green-500/20 to-green-400/10 rounded-full shadow-lg"
            >
              <BaseIcon
                icon="check-circle"
                class="text-4xl sm:text-5xl text-green-600"
              />
            </div>
            <!-- Success ring -->
            <div
              class="absolute inset-0 rounded-full border-2 border-green-500/30"
            ></div>
          </div>
        </div>

        <!-- Main heading with personalization -->
        <h1
          class="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-4 whitespace-pre-line lg:leading-tight"
        >
          {{ dynamicTitle }}
        </h1>

        <!-- Subheading with value proposition -->
        <h2 class="text-xl sm:text-2xl text-gray-700 mb-6 font-semibold">
          {{ dynamicSubtitle }}
        </h2>
      </div>

      <div class="max-w-7xl w-full gap-8 flex flex-col px-4 pb-8 mx-auto">
        <LandingPricing class="mx-auto" :is-title="false" is-paywall />

        <BaseHappy />

        <LandingFAQ class="z-[1]" />
      </div>

      <!-- Footer Section -->
      <div class="mt-8 pt-8 text-center">
        <div class="max-w-4xl mx-auto px-4">
          <!-- Legal Links -->
          <div
            class="flex flex-wrap justify-center items-center gap-4 mb-6 text-sm text-gray-500"
          >
            <router-link
              class="hover:text-gray-700 transition-colors duration-200 font-medium"
              target="_blank"
              :to="{ name: 'public.terms-of-services' }"
            >
              {{ $t('footer.termsOfUse') }}
            </router-link>
            <span class="text-gray-300">•</span>
            <router-link
              class="hover:text-gray-700 transition-colors duration-200 font-medium"
              target="_blank"
              :to="{ name: 'public.privacy-policy' }"
            >
              {{ $t('footer.links.privacyPolicy') }}
            </router-link>
          </div>

          <!-- Contact Information -->
          <div
            class="flex flex-wrap justify-center items-center gap-4 mb-6 text-sm text-gray-500"
          >
            <a
              href="mailto:capino.app@gmail.com"
              target="_blank"
              class="flex items-center gap-2 hover:text-gray-700 transition-colors duration-200"
            >
              <BaseIcon icon="envelope" class="w-4 h-4" />
              capino.app@gmail.com
            </a>
            <span class="text-gray-300">•</span>
            <a
              href="https://maps.app.goo.gl/Q1RB1tF8TdkqD1tk9"
              target="_blank"
              class="flex items-center gap-2 hover:text-gray-700 transition-colors duration-200"
            >
              <BaseIcon icon="marker" class="w-4 h-4" />
              {{ $t('footer.address') }}
            </a>
            <span class="text-gray-300">•</span>
            <div class="flex items-center gap-2">
              <BaseIcon icon="user" class="w-4 h-4" />
              {{ $t('footer.companyName') }}
            </div>
          </div>

          <!-- Copyright -->
          <div
            class="flex flex-wrap justify-center items-center gap-2 text-sm text-gray-500"
          >
            <span>© {{ new Date().getFullYear() }} Capino</span>
            <img
              src="/images/capybara-white-small.png"
              class="w-3 h-3"
              loading="lazy"
              alt="Capino"
            />
            <span>{{ $t('footer.copyright') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import BaseIcon from '@/components/Base/BaseIcon.vue'
import BaseHappy from '@/components/Base/BaseHappy.vue'
import LandingPricing from '@/components/Landing/LandingPricing.vue'
import LandingFAQ from '@/components/Landing/LandingFAQ.vue'

export default {
  name: 'HubPaywallLocked',

  components: { BaseIcon, BaseHappy, LandingPricing, LandingFAQ },

  computed: {
    ...mapState('user', ['user']),

    userReason() {
      if (!this.user?.quizString) return null

      try {
        const quizData = JSON.parse(this.user.quizString)
        return quizData.reason
      } catch (error) {
        console.error('Error parsing quiz string:', error)
        return null
      }
    },

    dynamicTitle() {
      const reasonTitles = {
        'exam-soon': 'Get ready for your exam! 🎉',
        explanations: 'Learn with clear\nexplanations 🎉',
        'all-grammar': 'All grammar in one place 🎉',
        other: 'Start your English\njourney! 🎉'
      }

      return reasonTitles[this.userReason] || "You're Almost There! 🎉"
    },

    dynamicSubtitle() {
      const reasonSubtitles = {
        'exam-soon': 'Quick, focused practice to make you succeed',
        explanations: 'Step-by-step guidance to understand grammar better',
        'all-grammar': 'Focused practice to boost your results quickly',
        other: 'Your personalized learning path is ready'
      }

      return (
        reasonSubtitles[this.userReason] ||
        'Your personalized learning path is ready'
      )
    }
  }
}
</script>

<style scoped>
@media (max-width: 768px) {
  .radial-gradient-overlay {
    background: radial-gradient(
      circle at center,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 1) 50%,
      rgba(255, 255, 255, 0.2) 60%,
      transparent 80%
    ) !important;
  }
}

@media (max-width: 1024px) {
  .radial-gradient-overlay {
    background: radial-gradient(
      circle at center,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 1) 40%,
      rgba(255, 255, 255, 0.2) 60%,
      transparent 80%
    );
  }
}

@media (min-width: 1025px) {
  .radial-gradient-overlay {
    background: radial-gradient(
      circle at center,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 1) 25%,
      rgba(255, 255, 255, 0.2) 60%,
      transparent 80%
    );
  }
}
</style>
